import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Stack,
  Box,
  Typography,
  IconButton,
  Collapse,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Icon } from "@iconify/react";
import chevronUp from "@iconify/icons-eva/chevron-up-fill";
import chevronDown from "@iconify/icons-eva/chevron-down-fill";

// A reusable card with a title, optional subtitle, an action slot (e.g. a
// granularity selector) and a collapse / expand toggle for its body.

const RootStyle = styled(Card)(({ theme }) => ({
  boxShadow: theme.customShadows.z4,
  borderRadius: theme.shape.borderRadiusMd,
  border: `1px solid ${theme.palette.grey[200]}`,
  background: theme.palette.background.paper,
  padding: theme.spacing(2),
  height: "100%",
  display: "flex",
  flexDirection: "column",
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(2.5),
  },
  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(3),
  },
}));

const CollapsibleCard = ({
  title,
  subtitle,
  action,
  children,
  defaultCollapsed = false,
  icon = null,
  sx = {},
}) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  return (
    <RootStyle sx={sx}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: collapsed ? 0 : 2 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
          {icon && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                color: "primary.main",
              }}
            >
              {icon}
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h6"
              noWrap
              sx={{
                fontWeight: 600,
                color: "text.primary",
                fontSize: { xs: "0.95rem", sm: "1rem", md: "1.1rem" },
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" sx={{ color: "text.secondary" }} noWrap>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
          {/* The action (e.g. granularity toggle) stays visible while collapsed */}
          {!collapsed && action}
          <Tooltip title={collapsed ? "Expand" : "Collapse"}>
            <IconButton
              size="small"
              onClick={() => setCollapsed((prev) => !prev)}
              aria-label={collapsed ? "expand section" : "collapse section"}
            >
              <Icon icon={collapsed ? chevronDown : chevronUp} width={22} height={22} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Collapse in={!collapsed} timeout="auto" unmountOnExit sx={{ flex: 1 }}>
        <Box sx={{ pt: 1 }}>{children}</Box>
      </Collapse>
    </RootStyle>
  );
};

CollapsibleCard.propTypes = {
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  action: PropTypes.node,
  children: PropTypes.node,
  defaultCollapsed: PropTypes.bool,
  icon: PropTypes.node,
  sx: PropTypes.object,
};

export default CollapsibleCard;
